<template>
  <div class="report-period-heatmap-wrap">
    <p
      v-if="title"
      class="report-period-heatmap__title"
    >
      {{ title }}
    </p>
    <div
      v-if="!rowData.length"
      class="alert alert-warning"
    >
      {{ emptyMessage }}
    </div>
    <ag-grid-vue
      v-else
      class="ag-theme-alpine report-period-heatmap"
      :class="gridClass"
      :style="{ width: '100%', height }"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :pinnedBottomRowData="pinnedBottom"
      :defaultColDef="defaultColDef"
      :headerHeight="resolvedHeaderHeight"
      :rowHeight="rowHeight"
      :tooltipShowDelay="tooltipShowDelay"
      :getRowStyle="getRowStyle"
      @cell-clicked="onCellClicked"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { getWeekNumber } from './reportPeriodHeatmapUtils';

export default {
  name: 'ReportPeriodHeatmap',
  components: { AgGridVue },
  props: {
    title: {
      type: String,
      default: '',
    },
    rowData: {
      type: Array,
      default() {
        return [];
      },
    },
    periods: {
      type: Array,
      default() {
        return [];
      },
    },
    pinnedBottom: {
      type: Array,
      default() {
        return [];
      },
    },
    periodicity: {
      type: String,
      default: 'Week',
    },
    rowLabelField: {
      type: String,
      default: 'BRAND_NAME',
    },
    rowLabelHeader: {
      type: String,
      default: 'Brand',
    },
    headerHeight: {
      type: Number,
      default: null,
    },
    rowHeight: {
      type: Number,
      default: 28,
    },
    height: {
      type: String,
      default: '360px',
    },
    emptyMessage: {
      type: String,
      default: 'There is no data to show.',
    },
    gridClass: {
      type: [ String, Array, Object ],
      default: '',
    },
    tooltipShowDelay: {
      type: Number,
      default: 200,
    },
    tooltipComponent: {
      type: [ Object, Function ],
      default: null,
    },
    tooltipFieldSuffix: {
      type: String,
      default: '_tooltip',
    },
    /** (value, params, periodKey) => string */
    valueFormatter: {
      type: Function,
      default: null,
    },
    /** (params, periodKey) => style object */
    cellStyle: {
      type: Function,
      default: null,
    },
    /** (params) => style object | null */
    getRowStyle: {
      type: Function,
      default: null,
    },
    /** Build period header; default Week adds week number line */
    periodHeaderName: {
      type: Function,
      default: null,
    },
    rowLabelCellStyle: {
      type: Object,
      default() {
        return {
          fontWeight: '400',
          fontSize: '14px',
          color: '#000000',
          textAlign: 'center',
          backgroundColor: '#F5F5F5',
        };
      },
    },
  },
  emits: ['cell-click'],
  computed: {
    resolvedHeaderHeight() {
      if (this.headerHeight != null) return this.headerHeight;
      return this.periodicity === 'Week' ? 48 : 36;
    },
    defaultColDef() {
      return {
        resizable: false,
        filter: false,
        sortable: false,
        flex: 1,
      };
    },
    columnDefs() {
      const periodCols = this.periods.map((z) => {
        const col = {
          field: z,
          headerName: this.resolvePeriodHeader(z),
          valueFormatter: (params) => this.formatValue(params, z),
          cellStyle: (params) => (this.cellStyle ? this.cellStyle(params, z) : null),
          wrapHeaderText: true,
        };
        if (this.tooltipComponent) {
          col.tooltipComponent = this.tooltipComponent;
          col.tooltipValueGetter = (params) => {
            const data = params.data;
            return (data && data[`${z}${this.tooltipFieldSuffix}`]) || null;
          };
        }
        return col;
      });
      return [
        {
          field: this.rowLabelField,
          headerName: this.rowLabelHeader,
          cellStyle: this.rowLabelCellStyle,
        },
        ...periodCols,
      ];
    },
  },
  methods: {
    resolvePeriodHeader(periodKey) {
      if (typeof this.periodHeaderName === 'function') {
        return this.periodHeaderName(periodKey, this.periodicity);
      }
      if (this.periodicity === 'Week') {
        return `${periodKey}\n${getWeekNumber(periodKey)}`;
      }
      return periodKey;
    },
    formatValue(params, periodKey) {
      if (typeof this.valueFormatter === 'function') {
        return this.valueFormatter(params.value, params, periodKey);
      }
      if (params.value === null || params.value === undefined) return '';
      return String(params.value);
    },
    onCellClicked(params) {
      if (!params || !params.data || (params.node && params.node.rowPinned)) return;
      const field = params.colDef && params.colDef.field;
      if (!field || field === this.rowLabelField) return;
      if (params.value === null || params.value === undefined) return;
      this.$emit('cell-click', {
        row: params.data,
        periodKey: field,
        value: params.value,
        data: params.data,
        params,
      });
    },
  },
};
</script>

<style>
.report-period-heatmap-wrap {
  padding-top: 3.4rem;
  padding-bottom: 1.5rem;
  margin-bottom: 0.5rem;
}

.report-period-heatmap__title {
  font-family: Roobert, Roobert-Light, 'Segoe UI', Arial, sans-serif !important;
  font-weight: 400 !important;
  font-size: 28px !important;
  line-height: 1.25;
  letter-spacing: 0;
  padding: 0;
  margin: 0 0 1rem;
  color: #262730;
  font-synthesis: none;
}

.report-period-heatmap {
  --ag-header-background-color: #f5f5f5;
  --ag-font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif;
  --ag-header-font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif;
  --ag-header-font-weight: 600;
  --ag-header-font-size: 16px;
  --ag-font-size: 14px;
  --ag-row-height: 28px;
  --ag-border-color: #ececec;
  --ag-row-border-color: #ececec;
  border: 1px solid #ececec;
  border-radius: 15px;
  overflow: hidden;
  margin: 0;
  font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif !important;
  font-size: 14px !important;
}

.report-period-heatmap.ag-theme-alpine,
.report-period-heatmap .ag-row,
.report-period-heatmap .ag-cell {
  font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif !important;
  font-size: 14px !important;
}

.report-period-heatmap .ag-root-wrapper {
  border: none;
  border-radius: 15px;
  overflow: hidden;
}

.report-period-heatmap .ag-cell {
  line-height: 28px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;
}

.report-period-heatmap .ag-header,
.report-period-heatmap .ag-header-cell,
.report-period-heatmap .ag-header-cell-label,
.report-period-heatmap .ag-header-cell-text {
  font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif !important;
  font-weight: 600 !important;
  font-size: 16px !important;
}

.report-period-heatmap .ag-cell:first-child {
  font-family: 'Source Sans 3', 'Source Sans Pro', 'Segoe UI', Arial, sans-serif !important;
  font-weight: 400 !important;
  font-size: 14px !important;
}

.report-period-heatmap .ag-header-cell-label {
  justify-content: center !important;
  text-align: center !important;
  width: 100%;
}

.report-period-heatmap .ag-header-cell-text {
  text-align: center !important;
  white-space: pre-line !important;
  line-height: 1.2 !important;
  width: 100%;
}
</style>
